import { useInstantSearchContext } from 'react-instantsearch';
import { useRouter } from 'next/router';
import { usePage } from '~/templates/pages';
import { useAlgoliaQuery } from '~/services/instantsearch/hooks';
import { ALGOLIA_INDEX_ID_PRODUCT_LIST } from '~/services/instantsearch/variables';

export const colorSwatchKey = 'cust_swatchimages.swatchKey';

interface IColorSwatch {
    key: string;
    imageUrl: string;
}

type AlgoliaResult = {
    facets?: Record<string, Record<string, number>>;
};

function processFacetsData(result: AlgoliaResult) {
    const colorSwatches = result?.facets?.[colorSwatchKey];

    const keys = Object.keys(colorSwatches ?? {});

    return keys.length
        ? keys.map((item) => {
              const [key, ...imageUrlParts] = item.split('|');
              return { key, imageUrl: imageUrlParts.join('|') } as IColorSwatch;
          })
        : [];
}

interface IColorSwatch {
    key: string;
    imageUrl: string;
}

const useColorSwatchesFallback = (shouldFetch: boolean) => {
    const page = usePage();
    const router = useRouter();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const category = page?.dataConfiguration || '';
    const query = router?.query?.query || '';

    const { data } = useAlgoliaQuery<any, IColorSwatch[]>(
        {
            ruleContexts: [category],
            facets: ['*'],
            hitsPerPage: 0,
        },
        {
            queryKey: [
                JSON.stringify({
                    ruleContexts: [category],
                    facets: ['*'],
                    hitsPerPage: 0,
                }),
            ],
            select: processFacetsData,
            enabled: shouldFetch && (!!category || !!query),
            gcTime: 1000 * 60 * 60,
        },
    );

    return data ?? [];
};

const useColorSwatches = () => {
    const instantSearchContext = useInstantSearchContext();
    const [results] =
        instantSearchContext?._initialResults?.[ALGOLIA_INDEX_ID_PRODUCT_LIST]?.results ?? [];
    // Always call the fallback hook so hook order never depends on `results`' truthiness,
    // but only let it actually fetch when there's no SSR-derived result to use instead.
    const fallbackSwatches = useColorSwatchesFallback(!results);
    const colorSwatches = results ? processFacetsData(results) : fallbackSwatches;

    return { colorSwatches };
};

export default useColorSwatches;
